import {
  DEFAULT_BREAKOUT,
  DEFAULT_ROOM_SETTINGS,
  type BreakoutSetup,
  type BreakoutState,
  type ChatMessage,
  type EndReason,
  type ErrorCode,
  type HostAction,
  type MeetingPublicInfo,
  type MediaStatePatch,
  type MediaTransportKind,
  type Participant,
  type RoomSettings,
  type RTCIceServerLike,
  type ServerMessage,
  type WaitingEntry,
} from '../../shared/protocol';
import { REDIS_OFF, type RedisAction, type RedistributionState } from '../../shared/redistribution';
import { api } from '../lib/api';
import { audioContext, LevelMeter, playBlip } from '../lib/audio';
import { describeMediaError, getCameraTrack, getMicrophoneTrack, getScreenStream, surfaceOf, type ScreenShareOptions } from '../lib/media';
import { isDesktopApp } from '../lib/desktop';
import { createImageShare } from '../lib/imageShare';
import { settingsStore } from '../lib/settings';
import { addRecentMeeting, getClientId } from '../lib/storage';
import { createStore, type Store } from '../lib/store';
import { recordingFileName } from '../lib/recording';
import { downloadedWhere, downloadFile, keepForSaving, savesWithButton, sizeLabel } from '../lib/recordingFiles';
import { dismissToast, toast } from '../lib/toast';
import { LiveKitMedia, liveKitStreamId, type StreamKind, type WantedMedia } from './LiveKitMedia';
import { Peer, type PeerStats } from './Peer';
import { MeetingRecorder, recordingSupported } from './Recorder';
import { SignalingClient, type SignalingStatus } from './SignalingClient';

export type Phase = 'idle' | 'connecting' | 'waiting' | 'in-meeting' | 'ended' | 'failed';
export type ConnectionState = 'online' | 'reconnecting' | 'offline';

export interface Reaction {
  key: number;
  from: string;
  emoji: string;
}

export interface MeetingState {
  phase: Phase;
  connection: ConnectionState;
  failure: { code: ErrorCode | 'network'; message: string } | null;
  ended: { reason: EndReason | 'left'; byName?: string } | null;
  meeting: MeetingPublicInfo | null;
  selfId: string | null;
  /** People in the same room as me (the main session or my breakout room): the call itself. */
  participants: Record<string, Participant>;
  /** Everyone in the meeting, whatever room they are in (breakout overview, counts). */
  everyone: Record<string, Participant>;
  breakout: BreakoutState;
  /** The coin redistribution board shared by everyone in the meeting. */
  redistribution: RedistributionState;
  waiting: WaitingEntry[];
  roomSettings: RoomSettings;
  locked: boolean;
  messages: ChatMessage[];
  unread: number;
  audioMuted: boolean;
  videoOn: boolean;
  micTrack: MediaStreamTrack | null;
  cameraTrack: MediaStreamTrack | null;
  screenStream: MediaStream | null;
  /** What our own share is: a captured screen/window or a still image (iPhone/iPad). */
  shareKind: 'screen' | 'image' | null;
  sharePaused: boolean;
  micError: string | null;
  cameraError: string | null;
  remoteStreams: Record<string, MediaStream>;
  peerStats: Record<string, PeerStats>;
  speaking: Record<string, boolean>;
  activeSpeakerId: string | null;
  /** When each person last started speaking (epoch ms): large galleries bring them to page 1. */
  spokeAt: Record<string, number>;
  reactions: Reaction[];
  unmuteRequestBy: string | null;
  /** My own recording of the meeting (on this device). */
  recording: 'recording' | 'paused' | null;
  joinedAt: number | null;
}

export interface JoinParams {
  meetingId: string;
  name: string;
  passcode?: string | null;
  hostKey?: string | null;
  micTrack: MediaStreamTrack | null;
  cameraTrack: MediaStreamTrack | null;
  audioMuted: boolean;
  videoOn: boolean;
}

const initialState = (): MeetingState => ({
  phase: 'idle',
  connection: 'online',
  failure: null,
  ended: null,
  meeting: null,
  selfId: null,
  participants: {},
  everyone: {},
  breakout: DEFAULT_BREAKOUT,
  redistribution: REDIS_OFF,
  waiting: [],
  roomSettings: DEFAULT_ROOM_SETTINGS,
  locked: false,
  messages: [],
  unread: 0,
  audioMuted: false,
  videoOn: false,
  micTrack: null,
  cameraTrack: null,
  screenStream: null,
  shareKind: null,
  sharePaused: false,
  micError: null,
  cameraError: null,
  remoteStreams: {},
  peerStats: {},
  speaking: {},
  activeSpeakerId: null,
  spokeAt: {},
  reactions: [],
  unmuteRequestBy: null,
  recording: null,
  joinedAt: null,
});

type AudioSink = HTMLAudioElement & { setSinkId?(id: string): Promise<void> };

/**
 * Client-side controller for one meeting: owns local media, the signaling socket and one
 * Peer per remote participant. React components read `store` and call the public methods.
 */
export class MeetingSession {
  readonly store: Store<MeetingState> = createStore(initialState());
  private signaling: SignalingClient;
  private peers = new Map<string, Peer>();
  private iceServers: RTCIceServerLike[] = [];
  /** Whether a relay exists; without one, failed direct connections cannot fall back. */
  turnConfigured = false;
  private params: JoinParams | null = null;
  private resumeToken: string | null = null;
  private readonly cameraCarrier = new MediaStream();
  private readonly screenCarrier = new MediaStream();
  private audioEls = new Map<string, AudioSink>();
  private meters = new Map<string, { track: MediaStreamTrack; meter: LevelMeter }>();
  private speakTimer: ReturnType<typeof setInterval> | null = null;
  private chatOpen = false;
  private sharePending = false;
  private stopImage: (() => void) | null = null;
  private waitingToasts = new Map<string, number>();
  private reactionKey = 0;
  private disposed = false;
  private unsubSettings: (() => void) | null = null;
  private lastOutputId = settingsStore.get().audioOutputId;
  /** "livekit": media goes through a LiveKit server instead of one Peer per participant. */
  media: MediaTransportKind = 'p2p';
  private lk: LiveKitMedia | null = null;
  /** LiveKit streams by participant, kept so audio can be re-attached when someone joins our room. */
  private lkStreams = new Map<string, Partial<Record<StreamKind, MediaStream>>>();
  private lkWanted: WantedMedia | null = null;
  private lkFailToast = 0;

  constructor() {
    this.signaling = new SignalingClient({
      onMessage: (m) => this.onMessage(m),
      onStatus: (s) => this.onSignalingStatus(s),
      onOpen: () => this.sendJoin(),
    });
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pointerdown', this.unlockAudio, true);
    // Diagnostics (and end-to-end tests): localStorage['nexo.debug'] = '1' exposes the session.
    try {
      if (localStorage.getItem('nexo.debug') === '1') (window as unknown as { __nexo: MeetingSession }).__nexo = this;
    } catch {
      /* storage blocked */
    }
    window.addEventListener('keydown', this.unlockAudio, true);
  }

  /** Safari/iOS block audio that starts without a user gesture; resume it on the next one. */
  private unlockAudio = () => {
    void audioContext().resume().catch(() => undefined);
    for (const el of this.audioEls.values()) if (el.paused && el.srcObject) void el.play().catch(() => undefined);
  };

  private get s(): MeetingState {
    return this.store.get();
  }

  private patch(p: Partial<MeetingState>): void {
    this.store.patch(p);
  }

  // ───────────────────────────── lifecycle ─────────────────────────────

  async join(params: JoinParams): Promise<void> {
    this.params = params;
    this.patch({
      ...initialState(),
      phase: 'connecting',
      micTrack: params.micTrack,
      cameraTrack: params.cameraTrack,
      audioMuted: params.audioMuted || !params.micTrack,
      videoOn: params.videoOn && !!params.cameraTrack,
    });
    if (params.micTrack) params.micTrack.enabled = !this.s.audioMuted;
    try {
      const cfg = await api.config();
      this.iceServers = cfg.iceServers;
      this.turnConfigured = cfg.turnConfigured;
      this.media = cfg.media ?? 'p2p';
    } catch (err) {
      this.patch({ phase: 'failed', failure: { code: 'network', message: (err as Error).message } });
      return;
    }
    if (this.disposed) return;
    if (this.media === 'livekit') this.startLiveKit();
    this.signaling.connect();
    this.speakTimer = setInterval(() => this.detectSpeakers(), 200);
    this.unsubSettings = settingsStore.subscribe(() => this.onSettingsChanged());
  }

  private sendJoin(): void {
    const p = this.params;
    if (!p) return;
    this.signaling.send({
      type: 'join',
      meetingId: p.meetingId,
      name: p.name,
      clientId: getClientId(),
      passcode: p.passcode ?? undefined,
      hostKey: p.hostKey ?? undefined,
      resumeToken: this.resumeToken ?? undefined,
      media: { audioMuted: this.s.audioMuted, videoOn: this.s.videoOn },
    });
  }

  leave(newHostId?: string): void {
    if (this.s.phase === 'in-meeting' || this.s.phase === 'waiting') this.signaling.send({ type: 'leave', newHostId });
    this.finish({ reason: 'left' });
  }

  endForAll(): void {
    this.host({ action: 'end' });
  }

  /** Releases every resource. Safe to call more than once. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.s.phase === 'in-meeting' || this.s.phase === 'waiting') this.signaling.send({ type: 'leave' });
    this.teardown();
    this.s.micTrack?.stop();
    this.s.cameraTrack?.stop();
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pointerdown', this.unlockAudio, true);
    window.removeEventListener('keydown', this.unlockAudio, true);
  }

  private finish(ended: MeetingState['ended']): void {
    this.teardown();
    this.s.micTrack?.stop();
    this.s.cameraTrack?.stop();
    this.patch({ phase: 'ended', ended, micTrack: null, cameraTrack: null, screenStream: null, remoteStreams: {}, participants: {}, everyone: {}, waiting: [] });
  }

  private teardown(): void {
    // Leaving or the meeting ending: what was recorded is still saved.
    void this.stopRecording(false);
    // Give the "leave" frame a moment to flush before closing the socket.
    setTimeout(() => this.signaling.close(), 150);
    this.closeAllPeers();
    this.stopLocalShare(false);
    this.lk?.dispose();
    this.lk = null;
    if (this.speakTimer) clearInterval(this.speakTimer);
    this.speakTimer = null;
    this.unsubSettings?.();
    this.unsubSettings = null;
    for (const { meter } of this.meters.values()) meter.dispose();
    this.meters.clear();
  }

  private onOnline = () => {
    this.patch({ connection: this.signaling.isOpen ? 'online' : 'reconnecting' });
    this.signaling.retryNow();
  };

  private onOffline = () => this.patch({ connection: 'offline' });

  private onPageHide = () => {
    if (this.s.phase === 'in-meeting' || this.s.phase === 'waiting') this.signaling.send({ type: 'leave' });
  };

  private onSignalingStatus(status: SignalingStatus): void {
    if (status === 'reconnecting' && (this.s.phase === 'in-meeting' || this.s.phase === 'waiting')) {
      this.patch({ connection: navigator.onLine ? 'reconnecting' : 'offline' });
    }
    if (status === 'reconnecting' && this.s.phase === 'connecting' && !this.resumeToken) {
      this.patch({ failure: { code: 'network', message: 'No se puede conectar con el servidor. Reintentando…' } });
    }
  }

  // ───────────────────────────── server events ─────────────────────────────

  private onMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'joined':
        return this.onJoined(msg);
      case 'waiting':
        this.resumeToken = msg.resumeToken;
        this.closeAllPeers();
        this.stopLocalShare(false);
        this.lk?.disconnect();
        return this.patch({ phase: 'waiting', meeting: msg.meeting, selfId: msg.selfId, connection: 'online', failure: null, participants: {}, everyone: {} });
      case 'participant-joined': {
        const p = msg.participant;
        const known = !!this.s.everyone[p.id];
        // A participant that reconnects builds new peer connections, so we do too.
        this.removePeer(p.id);
        this.setParticipant(p);
        if (!known && settingsStore.get().notifyJoinLeave && this.sameRoom(p)) toast(`${p.name} se ha unido a la reunión`);
        return;
      }
      case 'participant-updated':
        return this.onParticipantUpdated(msg.participant);
      case 'participant-left': {
        const wasHere = !!this.s.participants[msg.id];
        const everyone = { ...this.s.everyone };
        delete everyone[msg.id];
        this.patch({ everyone, activeSpeakerId: this.s.activeSpeakerId === msg.id ? null : this.s.activeSpeakerId });
        this.syncRoom();
        if (settingsStore.get().notifyJoinLeave && wasHere) {
          const text =
            msg.reason === 'removed'
              ? `${msg.name} ha sido retirado de la reunión`
              : msg.reason === 'moved-to-waiting'
                ? `${msg.name} se ha movido a la sala de espera`
                : msg.reason === 'timeout'
                  ? `${msg.name} ha perdido la conexión`
                  : `${msg.name} ha salido de la reunión`;
          toast(text);
        }
        return;
      }
      case 'waiting-updated': {
        const before = new Set(this.s.waiting.map((w) => w.id));
        const now = new Set(msg.waiting.map((w) => w.id));
        this.patch({ waiting: msg.waiting });
        // Admitted or gone: their "waiting" notice is no longer actionable.
        for (const [id, toastId] of this.waitingToasts) {
          if (!now.has(id)) {
            dismissToast(toastId);
            this.waitingToasts.delete(id);
          }
        }
        for (const w of msg.waiting) {
          if (!before.has(w.id)) {
            const toastId = toast(`${w.name} está en la sala de espera`, 'info', {
              action: { label: 'Admitir', run: () => this.host({ action: 'admit', target: w.id }) },
              duration: 15_000,
            });
            this.waitingToasts.set(w.id, toastId);
          }
        }
        return;
      }
      case 'room-updated': {
        const prev = this.s.roomSettings;
        this.patch({ roomSettings: msg.settings, locked: msg.locked });
        if (!this.isHost) {
          if (prev.allowChat !== msg.settings.allowChat) toast(msg.settings.allowChat ? 'El anfitrión ha activado el chat' : 'El anfitrión ha limitado el chat');
          if (prev.allowShare !== msg.settings.allowShare) toast(msg.settings.allowShare === 'all' ? 'Ya puedes compartir pantalla' : 'Solo el anfitrión puede compartir pantalla');
        }
        return;
      }
      case 'host-changed': {
        const host = this.s.everyone[msg.hostId];
        // Joining our own meeting as host is not news; being handed the role is.
        const alreadyHost = host?.role === 'host' && msg.previousHostId === null;
        if (msg.hostId === this.s.selfId) {
          if (!alreadyHost) toast('Ahora eres el anfitrión de la reunión', 'success');
        } else if (host && !(host.role === 'host' && msg.previousHostId === null)) {
          // Someone entering their own meeting with the host key is not news either.
          toast(`${host.name} es ahora el anfitrión`);
        }
        if (this.s.everyone[msg.hostId]) this.setParticipant({ ...this.s.everyone[msg.hostId], role: 'host' });
        if (msg.previousHostId && this.s.everyone[msg.previousHostId]) {
          this.setParticipant({ ...this.s.everyone[msg.previousHostId], role: 'participant' });
        }
        if (msg.hostId !== this.s.selfId && msg.previousHostId === this.s.selfId) this.patch({ waiting: [] });
        return;
      }
      case 'media-room':
        return this.lk?.connect(msg.url, msg.room, msg.token);
      case 'signal': {
        if (this.lk || !this.s.participants[msg.from]) return;
        const peer = this.peers.get(msg.from) ?? this.createPeer(msg.from);
        void peer.handleSignal(msg.data);
        return;
      }
      case 'chat':
        return this.onChat(msg.message);
      case 'breakout-updated':
        return this.onBreakoutUpdated(msg.breakout);
      case 'redistribution-updated':
        return this.patch({ redistribution: msg.redistribution });
      case 'breakout-help': {
        const room = this.s.breakout.rooms.find((r) => r.id === msg.roomId);
        toast(`✋ ${msg.name} pide ayuda en ${room?.name ?? 'su sala'}`, 'warning', {
          action: { label: 'Unirse', run: () => this.breakoutGo(msg.roomId) },
          duration: 20_000,
        });
        return;
      }
      case 'recording-stopped':
        if (!this.recorder) return;
        toast(`${msg.byName} ha detenido tu grabación. Se guarda lo grabado hasta ahora.`, 'warning', { duration: 10_000 });
        void this.stopRecording(false);
        return;
      case 'breakout-broadcast':
        toast(`Mensaje de ${msg.byName} para todas las salas: ${msg.text}`, 'info', { duration: 15_000 });
        return;
      case 'force-mute':
        this.applyMute(true, false);
        toast(`${msg.byName} te ha silenciado`, 'warning');
        return;
      case 'ask-unmute':
        return this.patch({ unmuteRequestBy: msg.byName });
      case 'moved-to-waiting':
        toast(`${msg.byName} te ha movido a la sala de espera`, 'warning');
        return;
      case 'reaction': {
        const key = ++this.reactionKey;
        this.patch({ reactions: [...this.s.reactions, { key, from: msg.from, emoji: msg.emoji }] });
        setTimeout(() => this.patch({ reactions: this.s.reactions.filter((r) => r.key !== key) }), 4000);
        return;
      }
      case 'notice':
        toast(msg.text, msg.level === 'warning' ? 'warning' : 'info');
        return;
      case 'meeting-ended':
        this.resumeToken = null;
        return this.finish({ reason: msg.reason, byName: msg.byName });
      case 'error':
        return this.onError(msg.code, msg.message);
      case 'pong':
        return;
    }
  }

  private onJoined(msg: Extract<ServerMessage, { type: 'joined' }>): void {
    const firstJoin = this.s.phase !== 'in-meeting';
    this.resumeToken = msg.resumeToken;
    this.closeAllPeers();
    if (!msg.resumed) this.stopLocalShare(false);
    const everyone: Record<string, Participant> = { [msg.self.id]: msg.self };
    for (const p of msg.participants) everyone[p.id] = p;

    this.patch({
      phase: 'in-meeting',
      connection: 'online',
      failure: null,
      meeting: msg.meeting,
      selfId: msg.self.id,
      everyone,
      breakout: msg.breakout,
      redistribution: msg.redistribution,
      waiting: msg.waiting,
      roomSettings: msg.settings,
      locked: msg.locked,
      remoteStreams: {},
      joinedAt: firstJoin ? Date.now() : this.s.joinedAt,
    });
    if (msg.self.audioMuted && !this.s.audioMuted) {
      this.applyMute(true, false);
      toast('El anfitrión silencia a los participantes al entrar', 'info');
    }
    this.syncRoom();
    if (msg.resumed && this.s.screenStream) this.requestShare();
    // Still recording after a reconnection: the others must keep seeing it.
    if (this.recorder) this.signaling.send({ type: 'recording', status: this.s.recording === 'paused' ? 'paused' : 'on' });
    this.publishMediaState({
      audioMuted: this.s.audioMuted,
      videoOn: this.s.videoOn,
      cameraStreamId: this.streamKey('camera'),
      screenStreamId: this.s.screenStream ? this.streamKey('screen') : null,
      sharePaused: this.s.sharePaused,
    });
    if (msg.resumed) {
      toast('Conexión restablecida', 'success');
    } else if (firstJoin && this.params) {
      addRecentMeeting({ id: msg.meeting.id, title: msg.meeting.title, at: new Date().toISOString() });
    }
  }

  private onParticipantUpdated(p: Participant): void {
    const prev = this.s.everyone[p.id];
    this.setParticipant(p);
    if (p.id === this.s.selfId && prev && prev.breakoutRoomId !== p.breakoutRoomId) {
      const room = this.s.breakout.rooms.find((r) => r.id === p.breakoutRoomId);
      toast(room ? `Estás en ${room.name}` : 'Has vuelto a la sesión principal', 'success');
    }
    if (p.id !== this.s.selfId) {
      if (!this.sameRoom(p)) return;
      if (prev && !prev.connected && p.connected === false) return;
      if (prev?.connected && !p.connected) toast(`${p.name} tiene problemas de conexión…`, 'warning');
      if (prev && !prev.sharing && p.sharing) toast(`${p.name} está compartiendo pantalla`);
      if (prev && !prev.handRaised && p.handRaised) toast(`✋ ${p.name} ha levantado la mano`);
      if (prev && !prev.recording && p.recording) toast(`● ${p.name} está grabando la reunión`, 'warning', { duration: 8000 });
      if (prev?.recording && !p.recording) toast(`${p.name} ha dejado de grabar`);
      return;
    }
    // The server is authoritative for our share (the host may stop it). Forced mutes arrive
    // as explicit "force-mute" messages, so stale audio flags here are ignored on purpose.
    if (p.sharing) this.sharePending = false;
    else if (this.s.screenStream && !this.sharePending) this.stopLocalShare(false);
  }

  private onChat(message: ChatMessage): void {
    const mine = message.from === this.s.selfId;
    this.patch({ messages: [...this.s.messages, message].slice(-500), unread: mine || this.chatOpen ? this.s.unread : this.s.unread + 1 });
    if (!mine) {
      if (settingsStore.get().chatSound) playBlip();
      if (!this.chatOpen) {
        const prefix = message.to === 'everyone' ? message.fromName : `${message.fromName} (en privado)`;
        toast(`${prefix}: ${message.text.slice(0, 80)}`, 'info');
      }
    }
  }

  private onError(code: ErrorCode, message: string): void {
    if (this.s.phase === 'connecting') {
      // Join rejected (bad passcode, locked, blocked…): stop retrying.
      this.signaling.close();
      this.patch({ phase: 'failed', failure: { code, message } });
      return;
    }
    if ((code === 'share-busy' || code === 'forbidden') && this.sharePending) {
      this.sharePending = false;
      this.stopLocalShare(false);
    }
    toast(message, code === 'forbidden' || code === 'share-busy' ? 'warning' : 'error');
  }

  private setParticipant(p: Participant): void {
    this.patch({ everyone: { ...this.s.everyone, [p.id]: p } });
    this.syncRoom();
  }

  private sameRoom(p: Participant): boolean {
    const me = this.s.selfId ? this.s.everyone[this.s.selfId] : null;
    return !!me && (p.breakoutRoomId ?? null) === (me.breakoutRoomId ?? null);
  }

  /**
   * The call is the people in my room. Keeps `participants` and the peer connections in step with
   * room changes (opening, moving, returning): peers with people elsewhere close, new ones open.
   */
  private syncRoom(): void {
    const participants: Record<string, Participant> = {};
    for (const p of Object.values(this.s.everyone)) if (this.sameRoom(p)) participants[p.id] = p;
    this.patch({ participants });
    if (this.lk) return this.syncLiveKitAudio();
    let changed = false;
    for (const id of [...this.peers.keys()]) {
      if (!participants[id]) {
        this.removePeer(id);
        changed = true;
      }
    }
    for (const id of Object.keys(participants)) {
      if (id !== this.s.selfId && !this.peers.has(id)) {
        this.createPeer(id);
        changed = true;
      }
    }
    if (changed) this.updateBitrates();
  }

  private onBreakoutUpdated(b: BreakoutState): void {
    const prev = this.s.breakout;
    this.patch({ breakout: b });
    if (prev.status === 'open' && b.status === 'closing') {
      toast(`El anfitrión ha cerrado las salas: volverás a la sesión principal en ${b.countdownSeconds} s`, 'warning', { duration: 10_000 });
    }
    if (this.isHost && prev.status === 'idle' && b.status === 'open') toast('Salas para grupos pequeños abiertas', 'success');
  }

  // ───────────────────────────── redistribution ─────────────────────────────

  /** A step on the redistribution board; the server checks it is allowed and tells everyone. */
  redistribute(action: RedisAction): void {
    this.signaling.send({ type: 'redistribution', action });
  }

  // ───────────────────────────── breakout rooms ─────────────────────────────

  breakoutSetup(setup: BreakoutSetup): void {
    this.host({ action: 'breakout-setup', setup });
  }

  breakoutOpen(): void {
    this.host({ action: 'breakout-open' });
  }

  breakoutClose(now = false): void {
    this.host({ action: 'breakout-close', now });
  }

  breakoutMove(target: string, roomId: string | null): void {
    this.host({ action: 'breakout-move', target, roomId });
  }

  breakoutBroadcast(text: string): void {
    const clean = text.trim();
    if (clean) this.host({ action: 'breakout-broadcast', text: clean });
  }

  /** Go to a room (null = main session). */
  breakoutGo(roomId: string | null): void {
    this.signaling.send({ type: 'breakout-go', roomId });
  }

  breakoutHelp(): void {
    this.signaling.send({ type: 'breakout-help' });
  }

  // ───────────────────────────── recording ─────────────────────────────

  private recorder: MeetingRecorder | null = null;
  private recordingStarted: Date | null = null;

  /** Recording works in this browser and the host allows it (the host always can). */
  canRecord(): boolean {
    return recordingSupported() && (this.isHost || this.s.roomSettings.allowRecording);
  }

  recordingElapsedMs(): number {
    return this.recorder?.elapsedMs ?? 0;
  }

  startRecording(): void {
    if (this.recorder) return;
    if (!recordingSupported()) return void toast('Este navegador no puede grabar. Usa Chrome, Edge, Firefox, Safari reciente o la app de escritorio.', 'warning');
    if (!this.canRecord()) return void toast('El anfitrión no permite que los participantes graben', 'warning');
    try {
      this.recorder = new MeetingRecorder(this.store);
      this.recorder.start();
    } catch (err) {
      this.recorder = null;
      return void toast(`No se pudo empezar a grabar: ${(err as Error).message}`, 'error');
    }
    this.recordingStarted = new Date();
    this.patch({ recording: 'recording' });
    this.signaling.send({ type: 'recording', status: 'on' });
    toast('Grabando la reunión en este equipo. Todos ven que estás grabando.', 'success');
  }

  pauseRecording(): void {
    if (!this.recorder || this.s.recording !== 'recording') return;
    this.recorder.pause();
    this.patch({ recording: 'paused' });
    this.signaling.send({ type: 'recording', status: 'paused' });
  }

  resumeRecording(): void {
    if (!this.recorder || this.s.recording !== 'paused') return;
    this.recorder.resume();
    this.patch({ recording: 'recording' });
    this.signaling.send({ type: 'recording', status: 'on' });
  }

  /** Stops and saves the file to the device (Downloads). */
  async stopRecording(notify = true): Promise<void> {
    const recorder = this.recorder;
    if (!recorder) return;
    this.recorder = null;
    this.patch({ recording: null });
    if (notify) this.signaling.send({ type: 'recording', status: null });
    const result = await recorder.stop();
    if (!result.blob.size) return void toast('La grabación quedó vacía y no se ha guardado', 'warning');
    const name = recordingFileName(this.s.meeting?.title ?? 'Reunión', this.recordingStarted ?? new Date(), result.extension);
    const file = new File([result.blob], name, { type: result.blob.type });
    // Phones and tablets: a «Guardar» button opens the share sheet (iOS blocks automatic downloads).
    if (savesWithButton()) return keepForSaving(file);
    downloadFile(file);
    toast(`Grabación guardada ${downloadedWhere()}: «${name}» (${sizeLabel(file.size)})`, 'success', { duration: 12_000 });
    if (result.extension === 'webm') {
      toast('Este navegador (Firefox) solo graba en formato WebM. Para MP4 graba desde Chrome, Edge, Safari o la app de escritorio.', 'info', { duration: 12_000 });
    }
  }

  // ───────────────────────────── peers ─────────────────────────────

  private createPeer(remoteId: string): Peer {
    const selfId = this.s.selfId!;
    const peer = new Peer(
      remoteId,
      selfId < remoteId,
      this.iceServers,
      {
        audio: this.s.micTrack,
        video: this.s.videoOn ? this.s.cameraTrack : null,
        screen: this.s.sharePaused ? null : (this.s.screenStream?.getVideoTracks()[0] ?? null),
        screenAudio: this.s.sharePaused ? null : (this.s.screenStream?.getAudioTracks()[0] ?? null),
        cameraCarrier: this.cameraCarrier,
        screenCarrier: this.screenCarrier,
      },
      {
        signal: (data) => this.signaling.send({ type: 'signal', to: remoteId, data }),
        track: (stream) => this.onRemoteStream(remoteId, stream),
        stats: (stats) => {
          if (this.peers.get(remoteId) === peer) this.patch({ peerStats: { ...this.s.peerStats, [remoteId]: stats } });
        },
      },
    );
    this.peers.set(remoteId, peer);
    return peer;
  }

  /** `key` is the id the sender announced (cameraStreamId/screenStreamId); `changed` forces a re-render. */
  private onRemoteStream(remoteId: string, stream: MediaStream, key = stream.id, changed = false): void {
    if (changed || this.s.remoteStreams[key] !== stream) {
      this.patch({ remoteStreams: { ...this.s.remoteStreams, [key]: stream } });
    }
    // Remote audio plays through hidden <audio> elements so the output device can be chosen.
    const audio = stream.getAudioTracks()[0];
    if (!audio) {
      stream.addEventListener('addtrack', () => this.onRemoteStream(remoteId, stream), { once: true });
      return;
    }
    // One element per stream: a participant's voice and the sound of their shared screen play side by side.
    const elKey = `${remoteId}:${stream.id}`;
    let el = this.audioEls.get(elKey);
    if (!el) {
      el = new Audio() as AudioSink;
      el.autoplay = true;
      this.audioEls.set(elKey, el);
    }
    if (el.srcObject !== stream) {
      el.srcObject = stream;
      void this.applySink(el);
      el.play().catch(() => toast('Toca la pantalla para activar el sonido de la reunión', 'warning'));
    }
    // Only the voice (camera stream) drives "who is speaking"; shared-screen sound must not.
    const p = this.s.participants[remoteId];
    const isScreen = !!p && (key === p.screenStreamId || (p.cameraStreamId !== null && key !== p.cameraStreamId));
    if (isScreen) return;
    const existing = this.meters.get(remoteId);
    if (existing?.track !== audio) {
      existing?.meter.dispose();
      this.meters.set(remoteId, { track: audio, meter: new LevelMeter(audio) });
    }
  }

  private removePeer(id: string): void {
    this.peers.get(id)?.close();
    this.peers.delete(id);
    this.removeRemoteAudio(id);
  }

  private removeRemoteAudio(id: string): void {
    for (const [key, el] of this.audioEls) {
      if (!key.startsWith(`${id}:`)) continue;
      el.srcObject = null;
      this.audioEls.delete(key);
    }
    this.meters.get(id)?.meter.dispose();
    this.meters.delete(id);
    const peerStats = { ...this.s.peerStats };
    delete peerStats[id];
    this.patch({ peerStats });
  }

  private closeAllPeers(): void {
    for (const id of [...this.peers.keys()]) this.removePeer(id);
    for (const [id, el] of this.audioEls) {
      el.srcObject = null;
      this.audioEls.delete(id);
    }
  }

  // ───────────────────────────── LiveKit (SFU) ─────────────────────────────

  /** Stream ids announced to the others: carrier stream ids (mesh) or ids derived from our id (LiveKit). */
  private streamKey(kind: StreamKind): string {
    if (this.lk && this.s.selfId) return liveKitStreamId(this.s.selfId, kind);
    return kind === 'camera' ? this.cameraCarrier.id : this.screenCarrier.id;
  }

  /** The stage reports whose cameras are on screen; with LiveKit only those videos are received. */
  setVisibleVideo(ids: string[] | null): void {
    this.lk?.setVisible(ids);
  }

  /** Name of the LiveKit room we are connected to (diagnostics and tests). */
  get mediaRoom(): string | null {
    return this.lk?.roomName ?? null;
  }

  private startLiveKit(): void {
    this.lk = new LiveKitMedia({
      stream: (identity, kind, stream) => {
        const entry = this.lkStreams.get(identity) ?? {};
        entry[kind] = stream;
        this.lkStreams.set(identity, entry);
        if (this.s.participants[identity]) this.onRemoteStream(identity, stream, liveKitStreamId(identity, kind), true);
      },
      gone: (identity) => {
        this.lkStreams.delete(identity);
        this.removeRemoteAudio(identity);
        const remoteStreams = { ...this.s.remoteStreams };
        delete remoteStreams[liveKitStreamId(identity, 'camera')];
        delete remoteStreams[liveKitStreamId(identity, 'screen')];
        this.patch({ remoteStreams });
      },
      stats: (identity, stats) => this.patch({ peerStats: { ...this.s.peerStats, [identity]: stats } }),
      needCredentials: () => this.signaling.send({ type: 'media-token' }),
      failed: (message) => {
        if (Date.now() - this.lkFailToast < 20_000) return;
        this.lkFailToast = Date.now();
        toast(message, 'warning');
      },
    });
    // Mirror our tracks onto LiveKit whenever one of them (or mute/pause) changes.
    this.store.subscribe(() => this.pushLiveKit());
  }

  private pushLiveKit(): void {
    if (!this.lk) return;
    const s = this.s;
    const wanted: WantedMedia = {
      mic: s.micTrack,
      micMuted: s.audioMuted,
      camera: s.videoOn ? s.cameraTrack : null,
      screen: s.screenStream?.getVideoTracks()[0] ?? null,
      screenAudio: s.screenStream?.getAudioTracks()[0] ?? null,
      screenPaused: s.sharePaused,
    };
    const prev = this.lkWanted;
    if (prev && (Object.keys(wanted) as (keyof WantedMedia)[]).every((k) => prev[k] === wanted[k])) return;
    this.lkWanted = wanted;
    this.lk.setLocal(wanted);
  }

  /**
   * Plays exactly the people in our room. LiveKit already limits us to our room's media; this
   * also silences someone the moment they move away, before the media server drops them.
   */
  private syncLiveKitAudio(): void {
    const here = this.s.participants;
    for (const key of [...this.audioEls.keys()]) {
      const id = key.slice(0, key.indexOf(':'));
      if (!here[id]) this.removeRemoteAudio(id);
    }
    for (const [identity, streams] of this.lkStreams) {
      if (!here[identity]) continue;
      for (const kind of ['camera', 'screen'] as const) {
        const stream = streams[kind];
        if (stream && !this.audioEls.has(`${identity}:${stream.id}`)) this.onRemoteStream(identity, stream, liveKitStreamId(identity, kind), true);
      }
    }
  }

  private updateBitrates(): void {
    if (this.lk) return;
    const others = this.peers.size;
    const hd = settingsStore.get().hdVideo;
    const camera = others <= 1 ? (hd ? 1500 : 800) : others <= 3 ? (hd ? 900 : 500) : 300;
    const screen = others <= 3 ? 1800 : 1000;
    for (const peer of this.peers.values()) peer.setBitrates(camera, screen);
  }

  private async applySink(el: AudioSink): Promise<void> {
    const id = settingsStore.get().audioOutputId;
    if (!el.setSinkId) return;
    try {
      await el.setSinkId(id && id !== 'default' ? id : '');
    } catch (err) {
      console.warn('[audio] no se pudo cambiar la salida', err);
    }
  }

  // ───────────────────────────── local media ─────────────────────────────

  get isHost(): boolean {
    const id = this.s.selfId;
    return !!id && this.s.participants[id]?.role === 'host';
  }

  private publishMediaState(state: MediaStatePatch): void {
    this.signaling.send({ type: 'media-state', state });
  }

  private applyMute(muted: boolean, publish = true): void {
    if (this.s.micTrack) this.s.micTrack.enabled = !muted;
    this.patch({ audioMuted: muted });
    if (publish) this.publishMediaState({ audioMuted: muted });
  }

  async toggleAudio(): Promise<void> {
    if (!this.s.audioMuted) return this.applyMute(true);
    const self = this.s.selfId ? this.s.participants[this.s.selfId] : null;
    if (self && self.role !== 'host' && !this.s.roomSettings.allowUnmute && self.audioMuted) {
      toast('El anfitrión no permite que los participantes reactiven su audio', 'warning');
      return;
    }
    if (!this.s.micTrack || this.s.micTrack.readyState === 'ended') {
      try {
        const track = await getMicrophoneTrack(settingsStore.get());
        await this.swapMic(track);
      } catch (err) {
        const message = describeMediaError(err, 'microphone');
        this.patch({ micError: message });
        toast(message, 'error', { action: { label: 'Reintentar', run: () => void this.toggleAudio() }, duration: 15_000 });
        return;
      }
    }
    this.patch({ unmuteRequestBy: null });
    this.applyMute(false);
  }

  private async swapMic(track: MediaStreamTrack): Promise<void> {
    const old = this.s.micTrack;
    track.enabled = !this.s.audioMuted;
    await Promise.all([...this.peers.values()].map((p) => p.replaceAudio(track)));
    this.patch({ micTrack: track, micError: null });
    if (old && old !== track) old.stop();
  }

  async toggleVideo(): Promise<void> {
    if (this.s.videoOn) {
      await Promise.all([...this.peers.values()].map((p) => p.replaceVideo(null)));
      this.s.cameraTrack?.stop();
      this.patch({ videoOn: false, cameraTrack: null });
      this.publishMediaState({ videoOn: false });
      return;
    }
    try {
      const track = this.s.cameraTrack?.readyState === 'live' ? this.s.cameraTrack : await getCameraTrack(settingsStore.get());
      await Promise.all([...this.peers.values()].map((p) => p.replaceVideo(track)));
      this.patch({ videoOn: true, cameraTrack: track, cameraError: null });
      this.publishMediaState({ videoOn: true });
    } catch (err) {
      const message = describeMediaError(err, 'camera');
      this.patch({ cameraError: message });
      toast(message, 'error', { action: { label: 'Reintentar', run: () => void this.toggleVideo() }, duration: 15_000 });
    }
  }

  /** Called when the user picks another device or changes audio processing in settings. */
  private async onSettingsChanged(): Promise<void> {
    const s = settingsStore.get();
    if (s.audioOutputId !== this.lastOutputId) {
      this.lastOutputId = s.audioOutputId;
      for (const el of this.audioEls.values()) void this.applySink(el);
    }
    const mic = this.s.micTrack;
    if (mic && mic.readyState === 'live') {
      const current = mic.getSettings();
      const wantedId = s.audioInputId || 'default';
      const changed =
        (wantedId !== 'default' && current.deviceId !== wantedId) ||
        current.echoCancellation !== s.echoCancellation ||
        current.noiseSuppression !== s.noiseSuppression ||
        current.autoGainControl !== s.autoGainControl;
      if (changed) {
        try {
          await this.swapMic(await getMicrophoneTrack(s));
        } catch (err) {
          toast(describeMediaError(err, 'microphone'), 'error');
        }
      }
    }
    const cam = this.s.cameraTrack;
    if (this.s.videoOn && cam && s.videoInputId && cam.getSettings().deviceId !== s.videoInputId) {
      try {
        const track = await getCameraTrack(s);
        await Promise.all([...this.peers.values()].map((p) => p.replaceVideo(track)));
        cam.stop();
        this.patch({ cameraTrack: track });
      } catch (err) {
        toast(describeMediaError(err, 'camera'), 'error');
      }
    }
    this.updateBitrates();
  }

  // ───────────────────────────── screen share ─────────────────────────────

  /** Checks the room rules before asking the browser for anything. */
  canShare(): boolean {
    if (this.s.screenStream) return false;
    const sharer = Object.values(this.s.participants).find((p) => p.sharing && p.id !== this.s.selfId);
    if (this.isHost) return true;
    if (this.s.roomSettings.allowShare === 'host') {
      toast('Solo el anfitrión puede compartir', 'warning');
      return false;
    }
    if (sharer) {
      toast(`${sharer.name} ya está compartiendo`, 'warning');
      return false;
    }
    return true;
  }

  /** Returns false when the person cancelled the picker or capture failed. */
  async startShare(opts: ScreenShareOptions = { surface: 'monitor', audio: false, optimizeVideo: false }): Promise<boolean> {
    if (!this.canShare()) return false;
    const stream = await this.captureScreen(opts);
    if (!stream) return false;
    this.beginShare(stream, 'screen');
    return true;
  }

  private async captureScreen(opts: ScreenShareOptions): Promise<MediaStream | null> {
    let stream: MediaStream;
    try {
      stream = await getScreenStream(opts);
    } catch (err) {
      if ((err as DOMException).name !== 'NotAllowedError') toast(describeMediaError(err, 'screen'), 'error');
      return null;
    }
    if (opts.audio && !stream.getAudioTracks().length) {
      const surface = surfaceOf(stream);
      toast(
        isDesktopApp()
          ? 'No se pudo capturar el sonido del equipo; se comparte solo la imagen. En Linux aún no es posible y en macOS hace falta la versión 13 o posterior.'
          : surface === 'window'
          ? 'Las ventanas no pueden compartir su sonido en el navegador. Para compartir audio, elige una pestaña o la pantalla completa.'
          : 'No se pudo capturar el sonido. Asegúrate de marcar «Compartir audio» en la ventana del navegador; en algunos sistemas solo las pestañas pueden compartir sonido.',
        'warning',
        { duration: 10_000 },
      );
    }
    return stream;
  }

  /** Zoom's "New share": pick another screen/window/tab without stopping the share. */
  async changeShareSource(opts: ScreenShareOptions): Promise<void> {
    if (!this.s.screenStream) return void this.startShare(opts);
    const stream = await this.captureScreen(opts);
    if (!stream) return;
    const old = this.s.screenStream;
    this.stopImage?.();
    this.stopImage = null;
    this.watchShareEnd(stream);
    this.patch({ screenStream: stream, shareKind: 'screen' });
    this.sendShareTracks();
    old.getTracks().forEach((t) => t.stop());
  }

  /** Freezes what the others see (last frame stays on their screen), like Zoom's "Pause share". */
  setSharePaused(paused: boolean): void {
    if (!this.s.screenStream || this.s.sharePaused === paused) return;
    this.patch({ sharePaused: paused });
    this.sendShareTracks();
    this.publishMediaState({ sharePaused: paused });
  }

  private sendShareTracks(): void {
    const stream = this.s.sharePaused ? null : this.s.screenStream;
    const video = stream?.getVideoTracks()[0] ?? null;
    const audio = stream?.getAudioTracks()[0] ?? null;
    for (const peer of this.peers.values()) {
      peer.setScreen(video, this.screenCarrier);
      peer.setScreenAudio(audio, this.screenCarrier);
    }
  }

  private watchShareEnd(stream: MediaStream): void {
    // The browser's own "Stop sharing" button ends the track.
    stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if (this.s.screenStream === stream) this.stopShare();
    });
  }

  /** Shares a photo or image (the sharing option available on iPhone and iPad). */
  async startImageShare(file: File): Promise<void> {
    if (!this.canShare()) return;
    try {
      const share = await createImageShare(file);
      this.stopImage = share.stop;
      this.beginShare(share.stream, 'image');
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  }

  private beginShare(stream: MediaStream, kind: 'screen' | 'image'): void {
    this.watchShareEnd(stream);
    this.patch({ screenStream: stream, shareKind: kind, sharePaused: false });
    this.sendShareTracks();
    this.requestShare();
    this.publishMediaState({ screenStreamId: this.streamKey('screen'), sharePaused: false });
  }

  /** Until the server confirms, stale "sharing: false" updates must not stop the capture. */
  private requestShare(): void {
    this.sharePending = true;
    this.signaling.send({ type: 'share-start' });
  }

  stopShare(): void {
    if (!this.s.screenStream) return;
    this.stopLocalShare(true);
  }

  private stopLocalShare(notify: boolean): void {
    const stream = this.s.screenStream;
    if (!stream) return;
    this.sharePending = false;
    this.stopImage?.();
    this.stopImage = null;
    stream.getTracks().forEach((t) => t.stop());
    for (const peer of this.peers.values()) {
      peer.setScreen(null, this.screenCarrier);
      peer.setScreenAudio(null, this.screenCarrier);
    }
    this.patch({ screenStream: null, shareKind: null, sharePaused: false });
    if (notify) this.signaling.send({ type: 'share-stop' });
  }

  // ───────────────────────────── chat & social ─────────────────────────────

  sendChat(to: 'everyone' | string, text: string): boolean {
    const clean = text.trim();
    if (!clean) return false;
    return this.signaling.send({ type: 'chat', to, text: clean });
  }

  setChatOpen(open: boolean): void {
    this.chatOpen = open;
    if (open && this.s.unread) this.patch({ unread: 0 });
  }

  setHandRaised(raised: boolean): void {
    this.signaling.send({ type: 'raise-hand', raised });
  }

  react(emoji: string): void {
    this.signaling.send({ type: 'reaction', emoji });
  }

  rename(name: string): void {
    this.signaling.send({ type: 'rename', name });
  }

  dismissUnmuteRequest(): void {
    this.patch({ unmuteRequestBy: null });
  }

  host(command: HostAction): void {
    if (!this.isHost) return void toast('Solo el anfitrión puede hacer esto', 'warning');
    this.signaling.send({ type: 'host', command });
  }

  // ───────────────────────────── speaking detection ─────────────────────────────

  private selfMeter: { track: MediaStreamTrack; meter: LevelMeter } | null = null;
  private lastLoudAt = new Map<string, number>();
  private spokeStart = new Map<string, number>();

  private detectSpeakers(): void {
    const s = this.s;
    if (s.phase !== 'in-meeting') return;
    const levels: Record<string, number> = {};
    for (const [id, { meter }] of this.meters) {
      if (s.participants[id] && !s.participants[id].audioMuted) levels[id] = meter.level();
    }
    if (s.selfId && s.micTrack && !s.audioMuted && s.micTrack.readyState === 'live') {
      if (this.selfMeter?.track !== s.micTrack) {
        this.selfMeter?.meter.dispose();
        this.selfMeter = { track: s.micTrack, meter: new LevelMeter(s.micTrack) };
      }
      levels[s.selfId] = this.selfMeter.meter.level();
    }
    // A short hold keeps the highlight steady across the gaps between syllables.
    const now = Date.now();
    const speaking: Record<string, boolean> = {};
    let loudest: string | null = null;
    for (const [id, level] of Object.entries(levels)) {
      if (level > 0.08) {
        if (now - (this.lastLoudAt.get(id) ?? 0) > 3000) this.spokeStart.set(id, now);
        this.lastLoudAt.set(id, now);
        if (!loudest || level > levels[loudest]) loudest = id;
      }
      if (now - (this.lastLoudAt.get(id) ?? 0) < 700) speaking[id] = true;
    }
    const changed = Object.keys(speaking).length !== Object.keys(s.speaking).length || Object.keys(speaking).some((id) => !s.speaking[id]);
    // The active speaker sticks until someone else talks, like other conferencing apps.
    const remoteLoudest = loudest && loudest !== s.selfId ? loudest : null;
    const active = remoteLoudest ?? (s.activeSpeakerId && s.participants[s.activeSpeakerId] ? s.activeSpeakerId : null);
    if (changed || active !== s.activeSpeakerId) {
      const spokeAt = { ...s.spokeAt };
      for (const id of Object.keys(speaking)) if (!s.speaking[id]) spokeAt[id] = this.spokeStart.get(id) ?? now;
      this.patch({ speaking, activeSpeakerId: active, spokeAt });
    }
  }
}
